/**
 * MessageBubble — 사용자 / AI 메시지 버블
 *
 * - user: 오른쪽 정렬, primary 색상
 * - assistant: 왼쪽 정렬, 카드 스타일
 *   - Markdown 렌더링 (MarkdownRenderer) — heading/list/bold/code/table + 인라인 Citation [N]
 *   - 위젯 목록 (WidgetRenderer)
 *   - Citation 목록 (CitationList)
 *   - 후속 질문 칩 (SuggestionChips)
 *   - 스트리밍 인디케이터
 */

import { Bot, User, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type {
  ChatMessage,
  AssistantMessage,
  MessageSegment,
  ExecutionStep,
} from "@modootoday/core-agent/types";
import { StreamingIndicator } from "./streaming-indicator";
import { SuggestionChips } from "./suggestion-chips";
import { CitationList } from "./inline-citation";
import { WidgetRenderer } from "@/agent/widgets/widget-renderer";
import { MarkdownRenderer } from "@/agent/ui/markdown-renderer";

export interface MessageBubbleProps {
  message: ChatMessage;
  onSend?: (text: string) => void;
  onDeepLink?: (path: string) => void;
  isStreamingActive?: boolean;
  className?: string;
}

function UserBubble({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  return (
    <div className={cn("flex justify-end", className)}>
      <div className="flex items-end gap-2 max-w-[80%]">
        <div className="rounded-2xl rounded-br-sm bg-accent px-4 py-2.5">
          <p className="text-[13px] leading-relaxed text-text-inverse whitespace-pre-wrap">
            {content}
          </p>
        </div>
        <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-surface-secondary mb-0.5">
          <User className="size-3.5 text-text-tertiary" />
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Segment Renderers — text / widget / step 각각을 inline으로 표시
   ══════════════════════════════════════════════════════════════════════════ */

function TextSegmentBubble({
  content,
  citations,
  onDeepLink,
  streamingCursor,
}: {
  content: string;
  citations: AssistantMessage["citations"];
  onDeepLink?: (path: string) => void;
  streamingCursor?: boolean;
}) {
  return (
    <div className="rounded-2xl rounded-tl-sm bg-surface-raised border border-stroke-subtle px-4 py-3">
      <div className="text-text-primary">
        <MarkdownRenderer
          content={content}
          citations={citations}
          onDeepLink={onDeepLink}
        />
        {streamingCursor && (
          <StreamingIndicator className="ml-1 inline-block" />
        )}
      </div>
    </div>
  );
}

function InlineStepIndicator({ step }: { step: ExecutionStep }) {
  const icon =
    step.status === "running" ? (
      <Loader2 className="size-3.5 animate-spin text-text-brand" />
    ) : step.status === "done" ? (
      <CheckCircle2 className="size-3.5 text-emerald-600" />
    ) : step.status === "error" ? (
      <XCircle className="size-3.5 text-red-500" />
    ) : (
      <Loader2 className="size-3.5 text-text-tertiary" />
    );

  const labelColor =
    step.status === "error"
      ? "text-red-600"
      : step.status === "done"
        ? "text-text-tertiary"
        : "text-text-primary";

  const label =
    step.summary ??
    (step.status === "running"
      ? `${step.tool} 실행 중...`
      : step.status === "done"
        ? `${step.tool} 완료`
        : step.status === "error"
          ? `${step.tool} 실패`
          : step.tool);

  return (
    <div className="flex items-center gap-2 rounded-lg bg-surface-secondary/40 px-3 py-2">
      {icon}
      <span className={cn("text-[12px] font-medium", labelColor)}>{label}</span>
      {step.duration !== undefined && step.status !== "running" && (
        <span className="text-[10px] text-text-tertiary tabular-nums">
          {step.duration}ms
        </span>
      )}
      {step.recordCount !== undefined && step.status === "done" && (
        <span className="ml-auto text-[10px] text-text-tertiary">
          {step.recordCount.toLocaleString()}건
        </span>
      )}
    </div>
  );
}

function RenderSegment({
  segment,
  isLastSegment,
  isStreaming,
  citations,
  onDeepLink,
}: {
  segment: MessageSegment;
  isLastSegment: boolean;
  isStreaming: boolean;
  citations: AssistantMessage["citations"];
  onDeepLink?: (path: string) => void;
}) {
  if (segment.kind === "text") {
    return (
      <TextSegmentBubble
        content={segment.content}
        citations={citations}
        onDeepLink={onDeepLink}
        streamingCursor={isLastSegment && isStreaming}
      />
    );
  }

  if (segment.kind === "step") {
    return <InlineStepIndicator step={segment.step} />;
  }

  if (segment.kind === "widget") {
    return <WidgetRenderer widget={segment.widget} onDeepLink={onDeepLink} />;
  }

  return null;
}

/* ══════════════════════════════════════════════════════════════════════════
   ThinkingIndicator — 다음 세그먼트 대기 중 시각적 피드백
   ══════════════════════════════════════════════════════════════════════════ */

function ThinkingIndicator() {
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full bg-surface-sunken/40 px-3 py-1.5"
      role="status"
      aria-label="응답 생성 중"
    >
      <div className="flex gap-1">
        <span className="size-1.5 animate-bounce rounded-full bg-text-tertiary/60 [animation-delay:-0.3s]" />
        <span className="size-1.5 animate-bounce rounded-full bg-text-tertiary/60 [animation-delay:-0.15s]" />
        <span className="size-1.5 animate-bounce rounded-full bg-text-tertiary/60" />
      </div>
      <span className="text-[11px] text-text-tertiary">응답 생성 중</span>
    </div>
  );
}

function shouldShowThinkingIndicator(
  segments: MessageSegment[],
  isStreaming: boolean,
): boolean {
  if (!isStreaming) return false;
  if (segments.length === 0) return false; // 초기 empty는 별도 처리
  const last = segments[segments.length - 1];
  if (!last) return false;
  // 마지막 text segment는 자체 streaming cursor 있음
  if (last.kind === "text") return false;
  // running step은 자체 spinner 있음
  if (last.kind === "step" && last.step.status === "running") return false;
  // step-done, widget 이후 다음 응답 기다리는 모든 경우
  return true;
}

function AssistantBubble({
  message,
  onSend,
  onDeepLink,
  isStreamingActive,
  className,
}: {
  message: AssistantMessage;
  onSend?: (text: string) => void;
  onDeepLink?: (path: string) => void;
  isStreamingActive?: boolean;
  className?: string;
}) {
  const segments = message.segments ?? [];
  const isEmpty = segments.length === 0 && message.isStreaming;
  const showThinking = shouldShowThinkingIndicator(
    segments,
    message.isStreaming,
  );

  return (
    <div className={cn("flex justify-start", className)}>
      <div className="flex items-start gap-2 max-w-[90%] w-full">
        {/* Avatar */}
        <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent/10 mt-0.5">
          <Bot className="size-3.5 text-text-brand" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* 초기 스트리밍 인디케이터 (이벤트 도착 전) */}
          {isEmpty && <ThinkingIndicator />}

          {/* Inline segments — text / step / widget을 수신 순서대로 렌더 */}
          {segments.map((segment, idx) => (
            <RenderSegment
              key={
                segment.kind === "step"
                  ? `step-${segment.step.id}`
                  : segment.kind === "widget"
                    ? `widget-${segment.widgetIndex}`
                    : `text-${idx}`
              }
              segment={segment}
              isLastSegment={idx === segments.length - 1}
              isStreaming={message.isStreaming}
              citations={message.citations}
              onDeepLink={onDeepLink}
            />
          ))}

          {/* 다음 세그먼트 대기 중 thinking indicator */}
          {showThinking && <ThinkingIndicator />}

          {/* Citation 목록 (완료 후) */}
          {!message.isStreaming && message.citations.length > 0 && (
            <CitationList
              citations={message.citations}
              onDeepLink={onDeepLink}
            />
          )}

          {/* 후속 질문 칩 (완료 후) */}
          {!message.isStreaming && message.suggestions.length > 0 && (
            <SuggestionChips
              suggestions={message.suggestions}
              onSelect={(s) => onSend?.(s)}
              disabled={isStreamingActive}
            />
          )}

          {/* 사용 토큰 (완료 후) */}
          {!message.isStreaming && message.usage && (
            <p className="text-[10px] text-text-tertiary">
              {(
                message.usage.promptTokens + message.usage.completionTokens
              ).toLocaleString()}{" "}
              토큰 사용
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function MessageBubble({
  message,
  onSend,
  onDeepLink,
  isStreamingActive,
  className,
}: MessageBubbleProps) {
  if (message.role === "user") {
    return <UserBubble content={message.content} className={className} />;
  }

  return (
    <AssistantBubble
      message={message as AssistantMessage}
      onSend={onSend}
      onDeepLink={onDeepLink}
      isStreamingActive={isStreamingActive}
      className={className}
    />
  );
}
